'use client';

import { buttonVariants } from '@/components/ui/button';
import { Dock, DockIcon } from '@/components/ui/dock';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { openWindow } from '@/redux/slice/windowSlice';
import { RootState } from '@/redux/store';
import { WindowData } from '@/utils/types/window';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { DockPosition, readDockIconPanels, readDockPinned, saveDockPinned } from '@/utils/dockPrefs';
import { ChevronLeft, ChevronRight, ChevronUp, Pin, PinOff } from 'lucide-react';
import Image from 'next/image';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSwipeable } from 'react-swipeable';
import useMainMenuPermissions from '../permissions/useMainMenuFormPermissions';
const EMPTY_PINNED: Record<DockPosition, boolean> = {
  bottom: false,
  left: false,
  right: false
};
const MainMenu = () => {
  const dispatch = useDispatch();
  const windows = useSelector((state: RootState) => state.windows.windows);
  const user = useCurrentUser();
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupWindows, setPopupWindows] = useState<WindowData[] | null>(null);
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [iconPanels, setIconPanels] = useState<Record<string, DockPosition>>({});
  const [panelPinned, setPanelPinned] = useState<Record<DockPosition, boolean>>(EMPTY_PINNED);
  const [panelVisible, setPanelVisible] = useState<Record<DockPosition, boolean>>(EMPTY_PINNED);

  // Load user-specific prefs once user ID is known
  useEffect(() => {
    if (!user?.id) return;
    const pinned = readDockPinned(user.id);
    setPanelPinned(pinned);
    setPanelVisible({
      bottom: pinned.bottom,
      left: pinned.left,
      right: pinned.right
    });
    setIconPanels(readDockIconPanels(user.id));
  }, [user?.id]);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const bottomDockRef = useRef<HTMLDivElement>(null);
  const leftDockRef = useRef<HTMLDivElement>(null);
  const rightDockRef = useRef<HTMLDivElement>(null);

  // Refs prevent stale closures in the global mousedown handler
  const panelVisibleRef = useRef(panelVisible);
  const panelPinnedRef = useRef(panelPinned);
  useEffect(() => {
    panelVisibleRef.current = panelVisible;
  }, [panelVisible]);
  useEffect(() => {
    panelPinnedRef.current = panelPinned;
  }, [panelPinned]);
  const {
    menuItems,
    isLoading
  } = useMainMenuPermissions();
  const filteredMenuItems = Object.entries(menuItems);
  const handlers = useSwipeable({
    onSwipedLeft: () => setPanelVisible(prev => ({
      ...prev,
      bottom: false
    })),
    onSwipedRight: () => setPanelVisible(prev => ({
      ...prev,
      bottom: true
    }))
  });

  // Single combined click-outside handler — reads state via refs, no stale closures
  useEffect(() => {
    const refs: Record<DockPosition, React.RefObject<HTMLDivElement>> = {
      bottom: bottomDockRef,
      left: leftDockRef,
      right: rightDockRef
    };
    const handleGlobalMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (popupRef.current && !popupRef.current.contains(target)) {
        setPopupVisible(false);
      }
      (['bottom', 'left', 'right'] as DockPosition[]).forEach(pos => {
        if (panelVisibleRef.current[pos] && !panelPinnedRef.current[pos]) {
          if (!refs[pos].current?.contains(target)) {
            setPanelVisible(prev => ({
              ...prev,
              [pos]: false
            }));
          }
        }
      });
    };
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleGlobalMouseDown);
    }, 100);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleGlobalMouseDown);
    };
  }, []);
  useEffect(() => {
    if (popupVisible && activePopup !== null) {
      const menuWindows = activePopup === 'all' ? windows.filter((w: WindowData) => !w.isWindowOpen) : windows.filter((w: WindowData) => w.title === activePopup);
      if (menuWindows.length > 0) setPopupWindows(menuWindows);
    }
  }, [windows]);
  useEffect(() => {
    setPopupWindows(windows);
  }, [windows]);

  // Sync icon panel assignments when QuickMenuSettings saves
  useEffect(() => {
    const handlePanelsChanged = () => {
      if (user?.id) setIconPanels(readDockIconPanels(user.id));
    };
    window.addEventListener('dockIconPanelsChanged', handlePanelsChanged);
    return () => window.removeEventListener('dockIconPanelsChanged', handlePanelsChanged);
  }, [user?.id]);
  const togglePin = (pos: DockPosition) => {
    if (!user?.id) return;
    const next = !panelPinned[pos];
    setPanelPinned(prev => ({
      ...prev,
      [pos]: next
    }));
    saveDockPinned(user.id, pos, next);
    if (!next) setPanelVisible(prev => ({
      ...prev,
      [pos]: false
    }));
  };
  const handleMenuClick = (e: React.MouseEvent, title: string, componentKey: string, pos: DockPosition) => {
    e.currentTarget.getBoundingClientRect();
    dispatch(openWindow({
      title,
      componentKey,
      id: null
    }));
    if (!panelPinned[pos]) setPanelVisible(prev => ({
      ...prev,
      [pos]: false
    }));
  };
  const handleContextMenu = (name: string, e: React.MouseEvent) => {
    e.preventDefault();
    const menuWindows = windows.filter((w: WindowData) => w.title === name);
    if (menuWindows.length > 0) {
      setActivePopup(name);
      setPopupWindows(menuWindows);
      setPopupVisible(true);
    }
  };
  const openPopupWindow = (title: string, componentKey: string, windowId: number, pos: DockPosition) => {
    dispatch(openWindow({
      title,
      componentKey,
      id: windowId
    }));
    setPopupVisible(false);
    if (!panelPinned[pos]) setPanelVisible(prev => ({
      ...prev,
      [pos]: false
    }));
  };

  // Popup opens away from the dock edge
  const popupPositionClass = (pos: DockPosition) => {
    if (pos === 'left') return 'left-full top-0 ml-2';
    if (pos === 'right') return 'right-full top-0 mr-2';
    return 'bottom-[66px] left-1/2 -translate-x-1/2';
  };
  const getItemsForPanel = (pos: DockPosition) => filteredMenuItems.filter(([, m]) => (iconPanels[m.module] ?? 'bottom') === pos);
  const tooltipSide = (pos: DockPosition): 'right' | 'left' | 'top' => {
    if (pos === 'left') return 'right';
    if (pos === 'right') return 'left';
    return 'top';
  };
  const renderMenuItems = (items: typeof filteredMenuItems, pos: DockPosition) => !isLoading && items.map(([name, mainmenu]) => {
    const IconComponent = mainmenu.icon;
    const win = windows.find((w: WindowData) => w.componentKey === mainmenu.module);
    const isWindowExist = !!win;
    return <DockIcon key={name}>
          <div className="relative">
            <Tooltip>
              <TooltipTrigger asChild>
                <button aria-label={mainmenu.name} className={cn(buttonVariants({
              variant: isWindowExist ? 'active' : 'bottomnav',
              size: 'icon'
            }), 'size-[24px] rounded-[6px] px-[3px] py-[3px] md:size-[32px] md:px-[5px] md:py-[5px] lg:size-11 lg:rounded-[8px] lg:px-2 lg:py-2')} onClick={e => handleMenuClick(e, isWindowExist ? win!.title : mainmenu.name, mainmenu.module, pos)} onContextMenu={e => handleContextMenu(mainmenu.name, e)}>
                  {IconComponent && <IconComponent />}
                </button>
              </TooltipTrigger>
              <TooltipContent side={tooltipSide(pos)}>
                <p>{mainmenu.name}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          {activePopup === mainmenu.name && popupVisible && popupWindows?.length ? <div ref={popupRef} className={cn('supports-backdrop-blur:bg-white/10 absolute z-50 flex max-h-[200px] flex-col gap-y-2 overflow-x-auto rounded-sm border-[1px] border-[#ddd] bg-[rgba(255,255,255,0.9)] px-4 py-3 shadow-lg backdrop-blur-md', popupPositionClass(pos))}>
              {popupWindows.map(visibleWindow => <button key={visibleWindow.id} onClick={() => openPopupWindow(visibleWindow.title, visibleWindow.componentKey, visibleWindow.id, pos)} className="flex min-w-[125px] flex-col items-center justify-center gap-y-1 rounded-[8px] bg-[#d5d5d578] px-3 py-2 text-xs font-semibold hover:bg-[#fff]">
                  <span className="text-xs text-[#2e2e2e]">{visibleWindow.title}</span>
                </button>)}
            </div> : null}
        </DockIcon>;
  });
  const renderControls = (pos: DockPosition) => <>
      <DockIcon key="all" data-sentry-element="DockIcon" data-sentry-source-file="MainMenu.tsx">
        <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="MainMenu.tsx">
          <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="MainMenu.tsx">
            <button aria-label="all-windows" className={cn(buttonVariants({
            variant: 'bottomnav',
            size: 'icon'
          }), 'size-[24px] rounded-[6px] px-[3px] py-[3px] md:size-[32px] md:px-[5px] md:py-[5px] lg:size-11 lg:rounded-[8px] lg:px-2 lg:py-2')} onClick={() => {
            setPopupVisible(true);
            setActivePopup('all');
          }}>
              <Image priority src="/images/waffle-icon.svg" alt="Home Icon" width={50} height={50} data-sentry-element="Image" data-sentry-source-file="MainMenu.tsx" />
            </button>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide(pos)} data-sentry-element="TooltipContent" data-sentry-source-file="MainMenu.tsx">
            <p>Show Open Windows</p>
          </TooltipContent>
        </Tooltip>
        {activePopup === 'all' && popupVisible && <div ref={popupRef} className={cn('supports-backdrop-blur:bg-white/10 absolute z-50 flex max-h-[300px] flex-col gap-y-2 overflow-x-auto rounded-sm border-[1px] border-[#ddd] bg-[rgba(255,255,255,0.9)] px-4 py-3 shadow-lg backdrop-blur-md', popupPositionClass(pos))}>
            {popupWindows?.length ? popupWindows.map(w => <button key={w.id} onClick={() => openPopupWindow(w.title, w.componentKey, w.id, pos)} className="flex min-w-[125px] flex-col items-center justify-center gap-y-1 rounded-[8px] bg-[#d5d5d578] px-3 py-2 text-xs font-semibold hover:bg-[#fff]">
                  <span className="text-xs text-[#2e2e2e]">{w.title}</span>
                </button>) : <p className="min-w-[125px] text-sm text-[#2e2e2e]">No hidden found</p>}
          </div>}
      </DockIcon>
      <DockIcon key="pin" data-sentry-element="DockIcon" data-sentry-source-file="MainMenu.tsx">
        <Tooltip data-sentry-element="Tooltip" data-sentry-source-file="MainMenu.tsx">
          <TooltipTrigger asChild data-sentry-element="TooltipTrigger" data-sentry-source-file="MainMenu.tsx">
            <button aria-label={panelPinned[pos] ? 'Unpin dock' : 'Pin dock open'} className={cn(buttonVariants({
            variant: panelPinned[pos] ? 'active' : 'bottomnav',
            size: 'icon'
          }), 'size-[24px] rounded-[6px] px-[3px] py-[3px] md:size-[32px] md:px-[5px] md:py-[5px] lg:size-11 lg:rounded-[8px] lg:px-2 lg:py-2')} onClick={() => togglePin(pos)}>
              {panelPinned[pos] ? <PinOff className="size-4 lg:size-5" /> : <Pin className="size-4 lg:size-5" />}
            </button>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide(pos)} data-sentry-element="TooltipContent" data-sentry-source-file="MainMenu.tsx">
            <p>{panelPinned[pos] ? 'Unpin dock' : 'Pin dock open'}</p>
          </TooltipContent>
        </Tooltip>
      </DockIcon>
    </>;
  const bottomItems = getItemsForPanel('bottom');
  const leftItems = getItemsForPanel('left');
  const rightItems = getItemsForPanel('right');
  return <div className="select-none" data-sentry-component="MainMenu" data-sentry-source-file="MainMenu.tsx">
      <TooltipProvider data-sentry-element="TooltipProvider" data-sentry-source-file="MainMenu.tsx">
        {/* BOTTOM PANEL */}
        <div {...handlers} className="relative">
          {!panelVisible.bottom && <>
              <button className="absolute left-0 top-1/2 z-50 flex h-32 w-5 -translate-y-1/2 transform items-center justify-center rounded-r-xl bg-gray-500 opacity-75 focus:outline-none lg:hidden" onClick={() => setPanelVisible(prev => ({
            ...prev,
            bottom: true
          }))} aria-label="Show dock">
                <Image src="/images/next.png" alt="Show dock" width={18} height={18} />
              </button>
              <button className="fixed bottom-0 left-1/2 hidden w-32 -translate-x-1/2 items-center justify-center rounded-t-[10px] bg-gray-500 opacity-75 focus:outline-none lg:flex" onClick={() => setPanelVisible(prev => ({
            ...prev,
            bottom: true
          }))} aria-label="Show dock">
                <ChevronUp className="h-6 w-6 text-white" />
              </button>
            </>}
          <div className={cn('fixed left-0 top-[54px] h-full w-auto flex-col p-0 transition-transform duration-300 md:top-[90px] lg:bottom-0 lg:top-auto lg:m-auto lg:flex lg:h-auto lg:w-full lg:gap-0 lg:py-1', panelVisible.bottom ? 'translate-x-0 lg:translate-y-[0px]' : '-translate-x-full lg:translate-x-0 lg:translate-y-[60px]')}>
            <Dock ref={bottomDockRef} direction="middle" className="m-0 flex h-auto flex-col gap-0 p-0 lg:m-auto lg:flex-row lg:py-1" data-sentry-element="Dock" data-sentry-source-file="MainMenu.tsx">
              {renderMenuItems(bottomItems, 'bottom')}
              {renderControls('bottom')}
            </Dock>
          </div>
        </div>

        {/* LEFT PANEL — only renders when icons assigned */}
        {leftItems.length > 0 && <>
            {!panelVisible.left && <button className="fixed left-0 top-1/2 z-50 hidden h-32 w-5 -translate-y-1/2 items-center justify-center rounded-r-xl bg-gray-500 opacity-75 focus:outline-none lg:flex" onClick={() => setPanelVisible(prev => ({
          ...prev,
          left: true
        }))} aria-label="Show left dock">
                <ChevronRight className="h-4 w-4 text-white" />
              </button>}
            <div className={cn('fixed left-0 top-1/2 z-40 hidden -translate-y-1/2 transition-transform duration-300 lg:flex', panelVisible.left ? 'translate-x-0' : '-translate-x-full')}>
              <Dock ref={leftDockRef} direction="middle" className="m-0 flex h-auto flex-col gap-1 p-2">
                {renderMenuItems(leftItems, 'left')}
                {renderControls('left')}
              </Dock>
            </div>
          </>}

        {/* RIGHT PANEL — only renders when icons assigned */}
        {rightItems.length > 0 && <>
            {!panelVisible.right && <button className="fixed right-0 top-1/2 z-50 hidden h-32 w-5 -translate-y-1/2 items-center justify-center rounded-l-xl bg-gray-500 opacity-75 focus:outline-none lg:flex" onClick={() => setPanelVisible(prev => ({
          ...prev,
          right: true
        }))} aria-label="Show right dock">
                <ChevronLeft className="h-4 w-4 text-white" />
              </button>}
            <div className={cn('fixed right-0 top-1/2 z-40 hidden -translate-y-1/2 transition-transform duration-300 lg:flex', panelVisible.right ? 'translate-x-0' : 'translate-x-full')}>
              <Dock ref={rightDockRef} direction="middle" className="m-0 flex h-auto flex-col gap-1 p-2">
                {renderMenuItems(rightItems, 'right')}
                {renderControls('right')}
              </Dock>
            </div>
          </>}
      </TooltipProvider>
    </div>;
};
export default memo(MainMenu);